import React, { useState, useCallback } from 'react';
import {
  Table, Button, Input, Space, Popconfirm, message, Modal, Form,
  InputNumber, Collapse, Tag, Select, Alert, Empty,
} from 'antd';
import { PlusOutlined, DeleteOutlined, DownloadOutlined, SafetyCertificateOutlined, EditOutlined } from '@ant-design/icons';
import {
  createPreschoolClass, updatePreschoolClass, deletePreschoolClass,
  getClassStudents, createClassStudents, updatePreschoolStudent, deletePreschoolStudent,
  generateClassCertificates, downloadCertificate,
} from '../../api/preschools';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * Sınıflar ve çocuklar.
 *
 * Buradaki çocuklar **ARYS öğrencisi DEĞİL** — ayrı tabloda duruyorlar ve
 * kurumun öğrenci listesine hiç karışmıyorlar (kullanıcının açık talimatı).
 * Yalnızca ad ve soyad tutuluyor: sertifikaya basılacak tek şey o, ve
 * toplanmayan veri sızdırılamaz.
 *
 * TOPLU EKLEME şart: bir sınıfta 20+ çocuk var ve liste okuldan hazır metin
 * olarak geliyor. Tek tek girdirmek bu ekranı kullanılamaz hâle getirirdi.
 */
const PreschoolClassesTab = ({ agreement, onChange }) => {
  const [ogrenciler, setOgrenciler] = useState({});   // classId -> []
  const [yukleniyor, setYukleniyor] = useState({});
  const [sinifModal, setSinifModal] = useState(false);
  // null = ekleme, kayit = duzenleme (ayni modal iki isi goruyor)
  const [duzenlenenSinif, setDuzenlenenSinif] = useState(null);
  const [duzenlenenOgrenci, setDuzenlenenOgrenci] = useState(null);
  const [ogrenciForm] = Form.useForm();
  const [topluModal, setTopluModal] = useState(null); // seçili sınıf
  const [sertifikaModal, setSertifikaModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [metin, setMetin] = useState('');
  const [donem, setDonem] = useState(1);
  const [form] = Form.useForm();

  const siniflar = agreement.classes || [];

  const ogrencileriYukle = useCallback(async (classId) => {
    setYukleniyor((o) => ({ ...o, [classId]: true }));
    try {
      const { data } = await getClassStudents(agreement.id, classId);
      setOgrenciler((o) => ({ ...o, [classId]: data || [] }));
    } catch {
      message.error('Öğrenciler yüklenemedi.');
    } finally {
      setYukleniyor((o) => ({ ...o, [classId]: false }));
    }
  }, [agreement.id]);

  const sinifModalAc = (kayit = null) => {
    setDuzenlenenSinif(kayit);
    form.resetFields();
    if (kayit) form.setFieldsValue({ name: kayit.name, student_count: kayit.student_count });
    setSinifModal(true);
  };

  const sinifKaydet = async (values) => {
    setSaving(true);
    try {
      if (duzenlenenSinif) {
        await updatePreschoolClass(agreement.id, duzenlenenSinif.id, values);
        message.success('Sınıf güncellendi.');
      } else {
        await createPreschoolClass(agreement.id, values);
        message.success('Sınıf eklendi.');
      }
      setSinifModal(false);
      setDuzenlenenSinif(null);
      form.resetFields();
      onChange();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Kaydedilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const ogrenciDuzenlemeyeAl = (o) => {
    setDuzenlenenOgrenci(o);
    ogrenciForm.setFieldsValue({ name: o.name, surname: o.surname });
  };

  const ogrenciKaydet = async (values) => {
    setSaving(true);
    try {
      await updatePreschoolStudent(agreement.id, duzenlenenOgrenci.id, values);
      message.success('Öğrenci güncellendi.');
      const sinifId = duzenlenenOgrenci.preschool_class_id;
      setDuzenlenenOgrenci(null);
      ogrenciForm.resetFields();
      if (sinifId) ogrencileriYukle(sinifId);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Güncellenemedi.'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Yapıştırılan metni satır satır ad/soyad'a böler. Son kelime soyad,
   * öncesi ad — "Ayşe Nur Yılmaz" doğru bölünsün diye.
   */
  const metniAyristir = (ham) => ham
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((satir) => {
      const parcalar = satir.split(/\s+/);
      const surname = parcalar.length > 1 ? parcalar.pop() : '';
      return { name: parcalar.join(' '), surname };
    })
    .filter((o) => o.name);

  const topluEkle = async () => {
    const liste = metniAyristir(metin);
    if (!liste.length) { message.warning('Liste boş.'); return; }

    setSaving(true);
    try {
      const { data } = await createClassStudents(agreement.id, topluModal.id, liste);
      message.success(data.message);
      setTopluModal(null);
      setMetin('');
      ogrencileriYukle(topluModal.id);
      onChange();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Öğrenciler eklenemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const sertifikaUret = async () => {
    setSaving(true);
    try {
      const { data } = await generateClassCertificates(agreement.id, sertifikaModal.id, { term_no: donem });
      message[data.message.includes('UYARI') ? 'warning' : 'success'](data.message);
      setSertifikaModal(null);
      ogrencileriYukle(sertifikaModal.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Sertifika üretilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const indir = async (sertifika) => {
    try {
      const { data } = await downloadCertificate(sertifika.id);
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sertifika.certificate_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Sertifika indirilemedi.'));
    }
  };

  const ogrenciKolonlari = (classId) => [
    { title: 'Ad', dataIndex: 'name' },
    { title: 'Soyad', dataIndex: 'surname' },
    {
      title: 'Sertifikalar',
      key: 'sertifika',
      render: (_, r) => (
        <Space wrap size={4}>
          {(r.certificates || []).length === 0 && <span className="text-gray-400">—</span>}
          {(r.certificates || []).map((s) => (
            s.pdf_path ? (
              <Button key={s.id} size="small" icon={<DownloadOutlined />} onClick={() => indir(s)}>
                {s.term_no}. Dönem
              </Button>
            ) : (
              <Tag key={s.id} color="orange">{s.term_no}. Dönem — PDF yok</Tag>
            )
          ))}
        </Space>
      ),
    },
    {
      title: '',
      key: 'islem',
      width: 100,
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => ogrenciDuzenlemeyeAl({ ...r, preschool_class_id: classId })}
          />
          <Popconfirm
            title="Öğrenci silinsin mi?"
            onConfirm={async () => {
              try {
                await deletePreschoolStudent(agreement.id, r.id);
                message.success('Silindi.');
                ogrencileriYukle(classId);
                onChange();
              } catch (error) {
                message.error(apiErrorMessage(error, 'Silinemedi.'));
              }
            }}
            okText="Sil"
            cancelText="Vazgeç"
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <span className="text-gray-500 text-sm">
          Bu çocuklar kurumun öğrenci listesine karışmaz; yalnızca sertifika
          için ad ve soyad tutulur.
        </span>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => sinifModalAc()}>
          Sınıf Ekle
        </Button>
      </div>

      {siniflar.length === 0 ? (
        <Empty description="Sınıf eklenmemiş." />
      ) : (
        <Collapse
          accordion
          /*
            ⚠ BOŞ DİZİ JAVASCRIPT'TE TRUTHY.

            Eskiden `if (key) ogrencileriYukle(Number(key))` yazıyordu.
            AntD Collapse paneli KAPATILIRKEN `onChange`'e boş dizi
            veriyor; `if ([])` geçiyor, `Number([])` ise **0** üretiyor ve
            istek `/preschool-agreements/2/classes/0/students` adresine
            gidiyordu. Kullanıcı bunu "sınıfa tıklayıp açınca ya da
            kapatınca hata alıyoruz" diye bildirdi (21.10.2026).

            Dizi/metin iki biçimi de geliyor (sürüme ve accordion moduna
            göre değişiyor), o yüzden ikisi birden ele alınıyor.
            `Number(undefined)` NaN, `Number('')` 0 — ikisi de falsy,
            yani kapatmada istek hiç atılmıyor.
          */
          onChange={(key) => {
            const sinifId = Number(Array.isArray(key) ? key[0] : key);
            if (sinifId) ogrencileriYukle(sinifId);
          }}
          items={siniflar.map((s) => ({
            key: String(s.id),
            label: (
              <Space>
                <span className="font-medium">{s.name}</span>
                <Tag>{s.students_count ?? 0} çocuk</Tag>
                {s.student_count > 0 && (
                  <span className="text-xs text-gray-400">mevcut: {s.student_count}</span>
                )}
              </Space>
            ),
            extra: (
              <Space onClick={(e) => e.stopPropagation()}>
                <Button size="small" icon={<EditOutlined />} onClick={() => sinifModalAc(s)} />
                <Button
                  size="small"
                  icon={<SafetyCertificateOutlined />}
                  onClick={() => { setSertifikaModal(s); setDonem(1); }}
                >
                  Sertifika Üret
                </Button>
                <Popconfirm
                  title="Sınıf silinsin mi?"
                  description="İçindeki çocuklar da erişilemez olur."
                  onConfirm={async () => {
                    try {
                      await deletePreschoolClass(agreement.id, s.id);
                      message.success('Sınıf silindi.');
                      onChange();
                    } catch (error) {
                      message.error(apiErrorMessage(error, 'Silinemedi.'));
                    }
                  }}
                  okText="Sil"
                  cancelText="Vazgeç"
                >
                  <Button danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
            children: (
              <>
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  className="mb-3"
                  onClick={() => { setTopluModal(s); setMetin(''); }}
                >
                  Öğrenci Ekle
                </Button>
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  loading={!!yukleniyor[s.id]}
                  dataSource={ogrenciler[s.id] || []}
                  columns={ogrenciKolonlari(s.id)}
                  locale={{ emptyText: 'Bu sınıfta öğrenci yok.' }}
                />
              </>
            ),
          }))}
        />
      )}

      {/* --- Sınıf ekle --- */}
      <Modal
        title={duzenlenenSinif ? 'Sınıfı Düzenle' : 'Sınıf Ekle'}
        open={sinifModal}
        onCancel={() => { setSinifModal(false); setDuzenlenenSinif(null); form.resetFields(); }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={sinifKaydet}>
          <Form.Item name="name" label="Sınıf Adı" rules={[{ required: true, message: 'Ad giriniz.' }]}>
            <Input placeholder="Papatya" autoFocus />
          </Form.Item>
          <Form.Item
            name="student_count"
            label="Mevcut"
            tooltip="Yalnızca bilgi amaçlı — ücret sabit aylık tutar, mevcuda bağlı değil."
          >
            <InputNumber min={0} max={200} style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving} block>Kaydet</Button>
        </Form>
      </Modal>

      {/* --- Toplu öğrenci ekle --- */}
      <Modal
        title={topluModal ? `${topluModal.name} — Öğrenci Ekle` : ''}
        open={!!topluModal}
        onCancel={() => { setTopluModal(null); setMetin(''); }}
        onOk={topluEkle}
        confirmLoading={saving}
        okText="Ekle"
        cancelText="Vazgeç"
      >
        <p className="text-gray-500 text-sm mb-2">
          Her satıra bir isim yazın ya da okuldan gelen listeyi yapıştırın.
          Son kelime soyad sayılır.
        </p>
        <Input.TextArea
          rows={10}
          value={metin}
          onChange={(e) => setMetin(e.target.value)}
          placeholder={'Ali Yılmaz\nAyşe Nur Şahin\nÇınar Öztürk'}
        />
        {metin.trim() && (
          <div className="text-sm text-gray-500 mt-2">
            {metniAyristir(metin).length} öğrenci eklenecek.
          </div>
        )}
      </Modal>

      {/* --- Sertifika üret --- */}
      <Modal
        title={sertifikaModal ? `${sertifikaModal.name} — Sertifika Üret` : ''}
        open={!!sertifikaModal}
        onCancel={() => setSertifikaModal(null)}
        onOk={sertifikaUret}
        confirmLoading={saving}
        okText="Üret"
        cancelText="Vazgeç"
      >
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message="Sınıfın tamamı için üretilir"
          description="Zaten sertifikası olan çocuk atlanır, ikinci numara yakılmaz. Şablon yüklenmemişse kayıt oluşur ama PDF üretilemez — şablonu yükleyip tekrar çalıştırabilirsiniz."
        />
        <Select
          value={donem}
          onChange={setDonem}
          style={{ width: '100%' }}
          options={Array.from({ length: agreement.certificate_term_count || 2 }, (_, i) => ({
            value: i + 1,
            label: `${i + 1}. Dönem`,
          }))}
        />
      </Modal>

      {/* --- Öğrenci düzenle --- */}
      <Modal
        title="Öğrenciyi Düzenle"
        open={!!duzenlenenOgrenci}
        onCancel={() => { setDuzenlenenOgrenci(null); ogrenciForm.resetFields(); }}
        footer={null}
      >
        <Form form={ogrenciForm} layout="vertical" onFinish={ogrenciKaydet}>
          <Form.Item name="name" label="Ad" rules={[{ required: true, message: 'Ad giriniz.' }]}>
            <Input autoFocus />
          </Form.Item>
          <Form.Item name="surname" label="Soyad" rules={[{ required: true, message: 'Soyad giriniz.' }]}>
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving} block>Kaydet</Button>
        </Form>
      </Modal>
    </div>
  );
};

export default PreschoolClassesTab;
